/**
 * Family profile completion checklist — shows a persistent scored progress
 * card until every essential piece is filled in. Each row deep-links to the
 * exact page to complete it.
 */
import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight, Sparkles } from "lucide-react";

const CHECKS = [
  {
    key: "contact",
    label: "Add parent contact info",
    detail: "Name, phone, preferred way to reach you",
    to: "/family/profile",
    check: (f) => !!(f?.parent_name && f?.parent_phone),
  },
  {
    key: "location",
    label: "Add your home address",
    detail: "So we can match sitters near you",
    to: "/family/profile",
    check: (f) => !!(f?.address && f?.city && f?.state && f?.zip_code),
  },
  {
    key: "child",
    label: "Add at least one child",
    detail: "Age, diagnosis, triggers, calming strategies",
    to: "/family/child/new",
    check: (_f, children) => (children?.length || 0) > 0,
  },
  {
    key: "home_instructions",
    label: "Home instructions",
    detail: "Keys, alarms, routines, house rules",
    to: "/family/profile",
    check: (f) => !!(f?.home_instructions || f?.household_rules),
  },
  {
    key: "medical",
    label: "Medical & emergency info",
    detail: "Insurance, pediatrician, nearest ER, allergies",
    to: "/family/profile",
    check: (f) => !!(f?.medical_info || f?.local_emergency_instructions),
  },
  {
    key: "emergency_contacts",
    label: "Emergency contacts",
    detail: "At least one person we can reach in an urgent moment",
    to: "/family/profile",
    check: (f) => (f?.emergency_contacts?.length || 0) > 0,
  },
  {
    key: "respite",
    label: "Respite services you need",
    detail: "Helps us surface the right sitters first",
    to: "/family/profile",
    check: (f) => (f?.respite_services_needed?.length || 0) > 0,
  },
];


export default function FamilyProfileCompletion({ family, children }) {
  const results = CHECKS.map((c) => ({ ...c, done: c.check(family, children) }));
  const doneCount = results.filter((r) => r.done).length;
  const total = results.length;
  const pct = Math.round((doneCount / total) * 100);
  const nextItem = results.find((r) => !r.done);

  if (doneCount === total) {
    return (
      <div className="mt-6 rounded-xl border border-sage-300 bg-sage-50 p-4 flex items-center gap-3" data-testid="family-completion-done">
        <Sparkles className="w-5 h-5 text-sage-600" />
        <div className="flex-1">
          <div className="font-heading font-semibold text-sage-900">Your profile is complete 🎉</div>
          <p className="text-xs text-stone2-700">Sitters see everything they need. Update anytime from Profile.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 paper-card p-5" data-testid="family-completion-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-heading font-bold text-sage-900 text-lg">
            Complete your family profile
          </div>
          <p className="text-sm text-stone2-600 mt-1">
            Sitters use this info to prepare — the more we know, the safer the match.
          </p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-heading font-bold text-sage-900" data-testid="family-completion-pct">{pct}%</div>
          <div className="text-xs text-stone2-500">{doneCount} of {total} complete</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-3 h-2 rounded-full bg-stone2-100 overflow-hidden">
        <div className="h-full bg-sage-600 transition-all duration-500"
          style={{ width: `${pct}%` }} data-testid="family-completion-bar" />
      </div>

      {/* Next-up CTA */}
      {nextItem && (
        <Link to={nextItem.to} data-testid="family-completion-next-cta"
          className="mt-4 rounded-lg bg-ochre-50 border border-ochre-200 p-3 flex items-center gap-3 hover:bg-ochre-100 transition-colors">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider font-bold text-ochre-700">Next step</div>
            <div className="font-heading font-semibold text-sage-900">{nextItem.label}</div>
            <div className="text-xs text-stone2-600">{nextItem.detail}</div>
          </div>
          <ArrowRight className="w-5 h-5 text-ochre-700" />
        </Link>
      )}

      {/* Full checklist */}
      <ul className="mt-4 space-y-2">
        {results.map((r) => (
          <li key={r.key} data-testid={`family-check-${r.key}`}>
            <Link to={r.to}
              className={`flex items-center gap-3 p-2 rounded-lg text-sm transition-colors ${
                r.done ? "text-stone2-500" : "text-sage-900 hover:bg-sage-50"
              }`}>
              {r.done ? (
                <CheckCircle2 className="w-5 h-5 text-sage-600 shrink-0" />
              ) : (
                <Circle className="w-5 h-5 text-stone2-300 shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className={r.done ? "line-through" : "font-semibold"}>{r.label}</div>
                {!r.done && <div className="text-xs text-stone2-500 mt-0.5">{r.detail}</div>}
              </div>
              {!r.done && <ArrowRight className="w-4 h-4 text-stone2-400 shrink-0" />}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
